import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Package, Purchases } from '@revenuecat/purchases-js'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from 'components'
import { useAnalytics } from 'hooks'
import { Check, Gift, Sparkles, X, Zap } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { useReferralProgram } from 'modules/referrals/use-referral-program'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { pricingDialogProps } from '../pricing-theme'
import { CREDITS_ADD_CTA } from '../constants'
import {
  closeTopUpModal,
  purchasePackage,
  selectShowTopUpModal,
  selectTopUpSource,
} from '../slices'
import { selectBusinessEntitlement, selectTotalCredits } from '../slices/credit-balance.slice'

interface TopUpPack {
  credits: number
  fallbackPrice: string
  perCredit: string
  savingsLabel?: string
  packageId: string
}

const TOP_UP_PACKS: TopUpPack[] = [
  {
    credits: 50,
    fallbackPrice: '$10',
    perCredit: '$0.20',
    packageId: 'topup_50',
  },
  {
    credits: 200,
    fallbackPrice: '$25',
    perCredit: '$0.125',
    packageId: 'topup_200',
  },
  {
    credits: 500,
    fallbackPrice: '$50',
    perCredit: '$0.10',
    savingsLabel: 'Best value · 50% off',
    packageId: 'topup_500',
  },
]

async function getTopUpPackages(): Promise<Package[]> {
  try {
    if (!Purchases.isConfigured()) return []
    const offerings = await Purchases.getSharedInstance().getOfferings()
    return offerings.all?.topups?.availablePackages ?? []
  } catch {
    return []
  }
}

export function TopUpModal() {
  const dispatch = useAppDispatch()
  const showModal = useAppSelector(selectShowTopUpModal)
  const topUpSource = useAppSelector(selectTopUpSource)
  const totalCredits = useAppSelector(selectTotalCredits)
  const entitlement = useAppSelector(selectBusinessEntitlement)
  const tier = (entitlement?.tier || 'Pro') as 'Pro' | 'Business'
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const { enabled: referralEnabled, info: referralInfo } = useReferralProgram()
  const referrerCredits = referralInfo?.referrerCredits ?? 0
  const [topUpPackages, setTopUpPackages] = useState<Package[]>([])
  const [purchasing, setPurchasing] = useState(false)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [selectedPack, setSelectedPack] = useState<string | null>(null)
  const trackedModalView = useRef(false)

  useEffect(() => {
    if (!showModal) {
      trackedModalView.current = false
      return
    }

    getTopUpPackages().then(pkgs => setTopUpPackages(pkgs))
    setPurchaseError(null)
    setSelectedPack(null)

    if (!trackedModalView.current) {
      trackedModalView.current = true
      track(AnalyticsEvents.Billing.TOPUP_MODAL_VIEWED, {
        credits_remaining: totalCredits,
        tier,
        source: topUpSource ?? 'billing_page',
      })
    }
  }, [showModal, totalCredits, tier, track, topUpSource])

  const findPackage = useCallback(
    (packageId: string): Package | null => {
      return topUpPackages.find(p => p.identifier === packageId) ?? null
    },
    [topUpPackages]
  )

  const handlePurchase = useCallback(
    async (pack: TopUpPack) => {
      const pkg = findPackage(pack.packageId)
      const price = pkg?.webBillingProduct?.price?.formattedPrice || pack.fallbackPrice

      track(AnalyticsEvents.Billing.TOPUP_PACK_SELECTED, {
        credits: pack.credits,
        price,
        tier,
      })

      track(AnalyticsEvents.Paywall.CHECKOUT_STARTED, {
        surface: 'in_app',
        source: 'top_up',
        credits: pack.credits,
        price: pack.fallbackPrice,
      })

      if (!pkg) {
        setPurchaseError('Credit packs are not available yet. They will be enabled soon.')
        return
      }

      setPurchasing(true)
      setSelectedPack(pack.packageId)
      setPurchaseError(null)

      try {
        const result = await dispatch(purchasePackage({ packageToPurchase: pkg }))

        if (result.meta.requestStatus === 'rejected') {
          if (result.payload === '__cancelled__') return
          throw new Error((result.payload as string) || 'Purchase failed')
        }

        track(AnalyticsEvents.Billing.TOPUP_PURCHASE_COMPLETED, {
          credits: pack.credits,
          price,
        })

        track(AnalyticsEvents.Paywall.CHECKOUT_COMPLETED, {
          surface: 'in_app',
          source: 'top_up',
          credits: pack.credits,
        })

        dispatch(closeTopUpModal())
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Purchase failed'
        setPurchaseError(message)

        track(AnalyticsEvents.Billing.TOPUP_PURCHASE_FAILED, {
          credits: pack.credits,
          error_message: message,
        })

        track(AnalyticsEvents.Paywall.CHECKOUT_FAILED, {
          surface: 'in_app',
          source: 'top_up',
          credits: pack.credits,
          error_message: message,
        })
      } finally {
        setPurchasing(false)
        setSelectedPack(null)
      }
    },
    [dispatch, findPackage, track, tier]
  )

  const handleClose = () => dispatch(closeTopUpModal())

  const handleReferralClick = () => {
    track(AnalyticsEvents.Billing.TOPUP_REFERRAL_NUDGE_CLICKED, {
      credits_remaining: totalCredits,
      source: topUpSource ?? 'billing_page',
    })
    dispatch(closeTopUpModal())
    navigate('/referrals')
  }

  return (
    <Dialog open={showModal} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          {...pricingDialogProps(
            'border-line bg-bg mx-auto w-full max-w-[calc(100vw-32px)] overflow-y-auto rounded-xl border p-0 shadow-[var(--shadow-card)] sm:max-w-lg sm:rounded-2xl'
          )}
          withCloseButton={false}
        >
          <DialogTitle className="sr-only">{CREDITS_ADD_CTA}</DialogTitle>
          <DialogDescription className="sr-only">
            Purchase additional credits for AI generation.
          </DialogDescription>

          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-3 text-paper-dim transition-colors hover:bg-bg-4 hover:text-paper"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-6 py-8 pt-12">
            <div className="mb-1 text-center">
              <h2 className="text-xl font-bold text-paper">{CREDITS_ADD_CTA}</h2>
              <p className="mt-1 text-sm text-paper-dim">
                You have <span className="font-semibold text-paper">{totalCredits} credits</span>{' '}
                remaining
              </p>
            </div>

            <div className="mt-6 space-y-3">
              {TOP_UP_PACKS.map(pack => {
                const pkg = findPackage(pack.packageId)
                const price = pkg?.webBillingProduct?.price?.formattedPrice || pack.fallbackPrice
                const isSelected = selectedPack === pack.packageId

                return (
                  <button
                    key={pack.packageId}
                    onClick={() => handlePurchase(pack)}
                    disabled={purchasing}
                    className={`relative flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all disabled:opacity-60 ${
                      pack.savingsLabel
                        ? 'bg-acid-tint border-acid/30 hover:border-acid/50'
                        : 'border-line bg-bg-3 hover:border-line-bright'
                    }`}
                  >
                    {pack.savingsLabel && (
                      <span className="text-ink-on-acid absolute -top-2 right-3 rounded-full bg-acid px-2 py-0.5 text-[10px] font-semibold">
                        {pack.savingsLabel}
                      </span>
                    )}

                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-acid/10">
                      <Sparkles size={18} className="text-acid" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-paper">
                          {pack.credits} credits
                        </span>
                      </div>
                      <span className="text-xs text-paper-faint">{pack.perCredit}/credit</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {isSelected && purchasing ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-acid/30 border-t-acid" />
                      ) : (
                        <span className="text-base font-bold text-paper">{price}</span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>

            {purchaseError && (
              <div className="alert mt-4" data-tone="error" role="alert">
                <p className="text-[13px]">{purchaseError}</p>
              </div>
            )}

            <div className="mt-5 space-y-1.5 text-center">
              <div className="flex items-center justify-center gap-1.5 text-xs text-paper-faint">
                <Check size={12} />
                <span>Purchased credits never expire</span>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-xs text-paper-faint">
                <Zap size={12} />
                <span>Used after your monthly credits are depleted</span>
              </div>
            </div>

            {referralEnabled && referrerCredits > 0 && (
              <button
                onClick={handleReferralClick}
                className="mt-5 flex w-full items-center justify-center gap-1.5 border-t border-line pt-4 text-xs text-paper-dim transition-colors hover:text-paper"
              >
                <Gift size={13} className="text-acid" />
                <span>
                  Or get them free. <span className="font-medium text-acid">Refer a friend</span>{' '}
                  and earn {referrerCredits} credits when they subscribe
                </span>
              </button>
            )}
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
